// src/pages/Presale.js
import React, { useEffect, useState } from "react";
import { ethers } from "ethers";
import useWallet from "../hooks/useWallet";
import MobileWarningBanner from "../components/MobileWarningBanner";
import "../styles/Presale.css";

const presaleAddress = "0xC2B9Dd791348D9C0a145f7E15455837a9d64E3F6";
const usdtAddress = "0xdAC17F958D2ee523a2206206994597C13D831ec7";
const usdcAddress = "0xA0b86991C6218B36C1d19D4a2e9Eb0cE3606eB48";

const abi = [
  "function getStageInfo(uint8) view returns (uint256,uint256,uint256,uint256,uint256,uint256,bool,uint256,uint256)",
  "function buyWithETH(uint256,uint256) payable",
  "function buyWithUSDT(uint256)",
  "function buyWithUSDC(uint256)",
  "function amountRaised() view returns (uint256)",
  "function tokensSold() view returns (uint256)",
  "function currentStage() view returns (uint8)",
  "function getLatestETHPrice() view returns (uint256)",
  "function stages(uint8) view returns (uint256,uint256,uint256,uint256,uint256,uint256,bool,uint256,uint256)"
];

const stages = [
  { name: "Seed Sale", index: 1 },
  { name: "Private Sale", index: 2 },
  { name: "Public Sale", index: 3 }
];

export default function Presale() {
  const { wallet, connectWallet, wcProvider } = useWallet();
  const [successTx, setSuccessTx] = useState(null);
  const [presaleInfo, setPresaleInfo] = useState(null);
  const [timeLeft, setTimeLeft] = useState("Loading...");
  const [token, setToken] = useState("ETH");
  const [amount, setAmount] = useState("");
  const [mcnAmount, setMcnAmount] = useState("");
  const [ethUsd, setEthUsd] = useState(0);
  const [currentStage, setCurrentStage] = useState(stages[0]);
  const [loading, setLoading] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile devices and tablets
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      // Enhanced detection for tablets
      const isMobileOrTablet = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Tablet|Mobile/i.test(userAgent) ||
        (navigator.maxTouchPoints && navigator.maxTouchPoints > 2) || // iPad Pro detection
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); // iPad with desktop mode
      
      setIsMobile(isMobileOrTablet);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get the correct provider based on connection type
  const getProvider = () => {
    if (wcProvider) {
      // WalletConnect provider
      return new ethers.providers.Web3Provider(wcProvider);
    } else if (window.ethereum) {
      // MetaMask or injected provider
      return new ethers.providers.Web3Provider(window.ethereum);
    } else {
      throw new Error("No provider available");
    }
  };

  const getButtonLabel = () => {
    if (!wallet) return "Connect Wallet";
    if (loading) return "Processing...";
    if (token !== "ETH" && !isApproved) return `Approve ${token}`;
    return "Confirm Purchase";
  };

  useEffect(() => {
    async function fetchEthPrice() {
      try {
        console.log("🔄 Fetching ETH price from:", process.env.REACT_APP_ETH_PROXY);
        const res = await fetch(process.env.REACT_APP_ETH_PROXY);
        
        // Check if response is ok
        if (!res.ok) {
          console.error("❌ API response not OK:", res.status, res.statusText);
          setEthUsd(2750); // Updated fallback
          return;
        }
        
        const text = await res.text();
        console.log("📥 Raw response text:", text);
        
        let data;
        try {
          data = JSON.parse(text);
        } catch (parseErr) {
          console.error("❌ Failed to parse JSON:", parseErr);
          console.log("Raw text was:", text);
          setEthUsd(2750); // Updated fallback
          return;
        }
        
        console.log("📥 Parsed API response:", data);
        
        // Try different possible response formats
        let price = null;
        
        // Format 1: { ethPrice: 2750 }
        if (data.ethPrice !== undefined) {
          price = parseFloat(data.ethPrice);
        }
        // Format 2: { price: 2750 }
        else if (data.price !== undefined) {
          price = parseFloat(data.price);
        }
        // Format 3: { data: { price: 2750 } }
        else if (data.data && data.data.price !== undefined) {
          price = parseFloat(data.data.price);
        }
        // Format 4: { result: 2750 }
        else if (data.result !== undefined) {
          price = parseFloat(data.result);
        }
        // Format 5: Direct number response
        else if (typeof data === 'number') {
          price = data;
        }
        
        console.log("💰 Extracted price:", price);

        if (!isNaN(price) && price > 0 && price < 100000) { // Sanity check
          setEthUsd(price);
          console.log("✅ ETH price set to:", price);
        } else {
          console.error("❌ Invalid ETH price:", price);
          console.log("Full data object:", JSON.stringify(data, null, 2));
          setEthUsd(2750); // Updated fallback
        }
      } catch (err) {
        console.error("❌ Failed to fetch ETH price:", err);
        setEthUsd(2750); // Updated fallback
      }
    }

    fetchEthPrice();
    // Refresh price every 30 seconds
    const interval = setInterval(fetchEthPrice, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchPresaleData();
    const interval = setInterval(fetchPresaleData, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  const fetchPresaleData = async () => {
    try {
      const provider = new ethers.providers.JsonRpcProvider("https://eth-mainnet.g.alchemy.com/v2/kaATe5M132ftD-hnmk2ymOYgRkt3uWnN");
      const readContract = new ethers.Contract(presaleAddress, abi, provider);

      const globalRaised = Number(await readContract.amountRaised()) / 1e18;
      const globalSold = Number(await readContract.tokensSold()) / 1e18;

      for (const stage of stages) {
        try {
          const data = await readContract.getStageInfo(stage.index);
          const isActive = data[6];
          if (!isActive) continue;

          const price = Number(data[0]) / 1e18;
          const total = Number(data[2]) / 1e18;
          const hardcap = Number(data[3]) / 1e18;
          const endTime = Number(data[5]);
          const sold = Number(data[7]) / 1e18;
          const raised = Number(data[8]) / 1e18;

          const now = Math.floor(Date.now() / 1000);
          const secondsLeft = endTime - now;
          const d = Math.floor(secondsLeft / 86400);
          const h = Math.floor((secondsLeft % 86400) / 3600);
          const m = Math.floor((secondsLeft % 3600) / 60);
          const s = secondsLeft % 60;

          setCurrentStage(stage);
          setPresaleInfo({ price, sold, total, raised, hardcap, globalRaised, globalSold });
          setTimeLeft(`${d}d ${h}h ${m}m ${s}s`);
          break;
        } catch (err) {
          console.error(`❌ Failed to load ${stage.name}:`, err);
        }
      }
    } catch (error) {
      console.error("Failed to fetch presale data:", error);
    }
  };

  useEffect(() => {
    if (!presaleInfo || !amount || amount === "") {
      setMcnAmount("");
      return;
    }
    
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setMcnAmount("");
      return;
    }
    
    let usdValue = amountNum;
    
    // For ETH, check if we have the ETH price
    if (token === "ETH") {
      if (!ethUsd || ethUsd === 0) {
        setMcnAmount("Loading price...");
        return;
      }
      usdValue = amountNum * ethUsd;
      console.log(`🧮 ETH Calculation: ${amountNum} ETH × ${ethUsd} = ${usdValue}`);
    }
    
    const mcn = usdValue / presaleInfo.price;
    console.log(`🧮 MCN Calculation: ${usdValue} ÷ ${presaleInfo.price} = ${mcn} MCN`);
    
    setMcnAmount(mcn.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  }, [amount, presaleInfo, token, ethUsd]);

  // Enhanced mobile-friendly connect wallet
  const handleConnectWallet = async () => {
    try {
      console.log("🔍 Connect wallet triggered");
      console.log("Is mobile/tablet:", isMobile);
      console.log("Has ethereum:", !!window.ethereum);
      console.log("User agent:", navigator.userAgent);
      
      // For mobile/tablet browsers without wallet
      if (isMobile && !window.ethereum) {
        console.log("📱 Mobile/Tablet detected without wallet, showing selection");
        
        // Use a more tablet-friendly selection method
        const walletOptions = [
          "1️⃣ MetaMask",
          "2️⃣ Trust Wallet", 
          "3️⃣ Rainbow Wallet",
          "4️⃣ Coinbase Wallet",
          "5️⃣ WalletConnect (QR Code)"
        ].join('\n');
        
        const walletChoice = prompt(
          `Select your wallet:\n\n${walletOptions}\n\nEnter number (1-5):`
        );
        
        console.log("User selected:", walletChoice);
        
        switch(walletChoice?.trim()) {
          case '1':
            // MetaMask deep link
            const metaMaskUrl = `https://metamask.app.link/dapp/${window.location.hostname}${window.location.pathname}`;
            window.location.href = metaMaskUrl;
            break;
          case '2':
            // Trust Wallet deep link
            window.location.href = `trust://open_url?coin_id=60&url=${window.location.href}`;
            break;
          case '3':
            // Rainbow Wallet deep link
            window.location.href = `rainbow://open?url=${window.location.href}`;
            break;
          case '4':
            // Coinbase Wallet deep link
            window.location.href = `https://go.cb-w.com/dapp?cb_url=${encodeURIComponent(window.location.href)}`;
            break;
          case '5':
          default:
            // Use WalletConnect but with better cleanup
            console.log("🔄 Initiating WalletConnect");
            
            // Clean up any existing WalletConnect modals first
            const existingModals = document.querySelectorAll('wcm-modal, w3m-modal');
            existingModals.forEach(modal => modal.remove());
            
            await connectWallet();
            
            // Force close modal after connection
            setTimeout(() => {
              const modals = document.querySelectorAll('wcm-modal, w3m-modal');
              modals.forEach(modal => {
                modal.style.display = 'none';
                modal.remove();
              });
            }, 2000);
            break;
        }
      } else {
        // Standard wallet connection
        console.log("💻 Standard connection flow");
        await connectWallet();
        
        // Also clean up modals for desktop/tablet
        setTimeout(() => {
          const modals = document.querySelectorAll('wcm-modal, w3m-modal');
          modals.forEach(modal => {
            modal.style.display = 'none';
          });
        }, 2000);
      }
    } catch (error) {
      console.error("Connection error:", error);
      alert("Failed to connect wallet. Please try again.");
    }
  };

  const handleBuy = async () => {
    if (!wallet) {
      alert("Please connect your wallet first.");
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      alert("❌ Please enter a valid amount.");
      return;
    }

    try {
      const _provider = getProvider();
      const _signer = _provider.getSigner();
      const contract = new ethers.Contract(presaleAddress, abi, _signer);

      // Check network with retry for mobile
      let network;
      let retries = 3;
      while (retries > 0) {
        try {
          network = await _provider.getNetwork();
          break;
        } catch (error) {
          retries--;
          if (retries === 0) throw error;
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      if (network.chainId !== 1) {
        const switchToMainnet = window.confirm(
          "⚠️ You're not on Ethereum Mainnet.\n\n" +
          "Would you like to switch to Mainnet now?"
        );
        
        if (switchToMainnet) {
          try {
            await window.ethereum.request({
              method: 'wallet_switchEthereumChain',
              params: [{ chainId: '0x1' }],
            });
            // Wait for switch to complete
            await new Promise(resolve => setTimeout(resolve, 1000));
          } catch (switchError) {
            if (switchError.code === 4902) {
              alert("Please add Ethereum Mainnet to your wallet.");
            }
            return;
          }
        } else {
          return;
        }
      }

      let value;
      try {
        value = ethers.utils.parseUnits(amount.trim(), token === "ETH" ? 18 : 6);
      } catch (parseError) {
        alert("❌ Invalid amount format. Please enter numbers only.");
        return;
      }

      setLoading(true);
      
      // Get gas prices with mobile-friendly settings
      const feeData = await _provider.getFeeData();
      const maxPriorityFeePerGas = feeData.maxPriorityFeePerGas || ethers.utils.parseUnits("2", "gwei");
      const maxFeePerGas = feeData.maxFeePerGas || ethers.utils.parseUnits("50", "gwei");

      if (token === "ETH") {
        const ethValue = ethers.utils.parseEther(amount);
        const latestPrice = Math.round(ethUsd * 1e8);
        const deadline = Math.floor(Date.now() / 1000) + 600; // 10 minute deadline

        const tx = await contract.buyWithETH(
          latestPrice,
          deadline,
          {
            value: ethValue,
            gasLimit: 350000, // Increased gas limit for mobile
            maxPriorityFeePerGas,
            maxFeePerGas
          }
        );
        
        // Show transaction pending message
        alert("📤 Transaction submitted! Waiting for confirmation...");
        
        await tx.wait();
        setSuccessTx(tx.hash);
        alert("✅ Purchase successful!");
      } else {
        const tokenAbi = ["function approve(address spender, uint256) public returns (bool)"];
        const tokenAddress = token === "USDT" ? usdtAddress : usdcAddress;
        const tokenContract = new ethers.Contract(tokenAddress, tokenAbi, _signer);

        if (!isApproved) {
          const approveTx = await tokenContract.approve(presaleAddress, value);
          alert("📤 Approval transaction submitted! Please wait...");
          await approveTx.wait();
          alert(`✅ ${token} approved! Now click Buy again to confirm your purchase.`);
          setIsApproved(true);
          return;
        }

        const buyTx = token === "USDT"
          ? await contract.buyWithUSDT(value, { gasLimit: 250000, maxPriorityFeePerGas, maxFeePerGas })
          : await contract.buyWithUSDC(value, { gasLimit: 250000, maxPriorityFeePerGas, maxFeePerGas });
        
        alert("📤 Purchase transaction submitted! Please wait...");
        await buyTx.wait();
        alert(`✅ Purchase with ${token} confirmed!`);
        setSuccessTx(buyTx.hash);
        setIsApproved(false);
      }
      
      fetchPresaleData();
      setAmount("");
      setMcnAmount("");
    } catch (error) {
      console.error("Transaction failed:", error);
      
      // Mobile-friendly error messages
      if (error.code === 4001 || error.message?.includes("rejected")) {
        alert("❌ Transaction cancelled");
      } else if (error.message?.includes("insufficient funds")) {
        alert("❌ Insufficient balance for transaction + gas fees");
      } else if (error.code === -32603) {
        alert("❌ Transaction failed. Please check your balance and try again.");
      } else {
        alert(error?.reason || error?.message || "❌ Transaction failed. Please try again.");
      }
      setIsApproved(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="bg-[#0c071f] text-white rounded-2xl px-6 py-6 mb-8 shadow-lg">
        <h1 className="text-2xl md:text-3xl font-bold text-purple-400 flex items-center gap-2">
          💰 MediCoin Presale Dashboard
        </h1>
        <p className="mt-2 text-sm md:text-base text-gray-300 leading-relaxed">
          Join the revolution of decentralized healthcare. By participating in the MediCoin presale, you're not just investing in a utility token —
          <strong className="text-purple-400"> you're becoming a founding force behind MediCureOn's AI-powered, blockchain-secured, patient-centric future.</strong>
          This is your chance to be early, to be bold, and to shape what's next in global wellness.
        </p>
      </div>
      
      <MobileWarningBanner />
      
      <div className="presale-wrapper">
        <div className="presale-info-box">
          <h3>How to Buy</h3>
          <ul className="presale-instructions text-white space-y-3 text-sm md:text-base leading-relaxed">
            <li>
              🦊 <strong className="text-purple-400">Connect your wallet</strong> using MetaMask, Trust Wallet, Rainbow, Coinbase Wallet, or any Web3-compatible wallet.
              {isMobile && (
                <span className="block text-xs text-gray-400 mt-1">
                  Mobile tip: Use your wallet's built-in browser or select your wallet when connecting
                </span>
              )}
            </li>
            <li>
              💱 <strong className="text-purple-400">Choose a payment method</strong>: ETH, USDT
            </li>
            <li>
              ✍️ <strong className="text-purple-400">Enter the amount</strong> you'd like to invest.
            </li>
            <li>
              🔐 <strong className="text-purple-400">Using USDT</strong> You'll need to approve the transaction **first** — this grants the contract permission to use your stablecoins.  
              <br />Once approved, you'll be able to <strong className="text-purple-400">click 'Confirm Purchase'</strong> to complete your investment.
            </li>
          </ul>

          <p className="notice mt-4 text-sm text-gray-300">
            🔒 All tokens purchased during the presale are locked and will unlock gradually based on our vesting schedule after TGE.
          </p>
        </div>

        <div className="presale">
          <h2>{currentStage.name}</h2>
          <p className="price">
            {presaleInfo ? `1 MCN = $${presaleInfo.price.toFixed(3)}` : "Loading price..."}
          </p>
          <div className="countdown">⏱️ {currentStage.name} Ends In: {timeLeft}</div>

          <div className="progressbar">
            <div className="progress" style={{ width: presaleInfo ? `${(presaleInfo.sold / presaleInfo.total) * 100}%` : "0%" }}></div>
          </div>

          <div className="info">
            {presaleInfo ? (
              <>
                <p>
                  <strong>Stage Raised:</strong> ${presaleInfo.raised.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / 
                  ${presaleInfo.hardcap.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p>
                  <strong>Stage Tokens Sold:</strong> {presaleInfo.sold.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / 
                  {presaleInfo.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                {token === "ETH" && (
                  <p className="eth-price-info">
                    <strong>ETH Price:</strong> ${ethUsd > 0 ? ethUsd.toFixed(2) : "Loading..."}
                  </p>
                )}
              </>
            ) : <p>Loading presale data...</p>}
          </div>

          <div className="token-buttons">
            {['ETH', 'USDT'].map(t => (
              <button 
                key={t} 
                onClick={() => {
                  setToken(t);
                  setIsApproved(false);
                }} 
                className={token === t ? "active" : ""}
              >
                <img src={`/assets/icons/${t.toLowerCase()}.svg`} alt={t} className="token-icon" /> {t}
              </button>
            ))}
          </div>

          {successTx && (
            <div className="confirmation-box">
              ✅ Purchase confirmed!<br />
              <a href={`https://etherscan.io/tx/${successTx}`} target="_blank" rel="noopener noreferrer">
                View on Etherscan
              </a>
            </div>
          )}

          <div className="input-section">
            <label>Amount in {token} You Pay:</label>
            <input 
              type="number" 
              inputMode="decimal"
              pattern="[0-9]*[.,]?[0-9]*"
              value={amount} 
              onChange={(e) => setAmount(e.target.value)} 
              placeholder="Enter amount"
              disabled={loading}
            />
          </div>

          <div className="input-section">
            <label>Amount in $MCN You Receive:</label>
            <input type="text" value={mcnAmount} readOnly />
          </div>

          {!wallet ? (
            <button onClick={handleConnectWallet} className="buy-btn">
              <img src="/assets/icons/wallet.svg" alt="wallet" className="wallet-icon" /> Connect Wallet
            </button>
          ) : (
            <button onClick={handleBuy} className="buy-btn" disabled={loading}>
              {getButtonLabel()}
            </button>
          )}
        </div>
      </div>
    </>
  );
}